import type { Href } from "expo-router";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useMemo, useState, type ReactNode } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";
import { COLORS, FONTS } from "../../../lib/brand";
import Svg, { Circle, G, Path, Rect } from "react-native-svg";

import { AuthCard } from "../../../components/auth/AuthCard";
import { ClientSignupForm } from "../../../components/auth/ClientSignupForm";
import { StudentSignupForm } from "../../../components/auth/StudentSignupForm";

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

function signupHrefWithRole(nextRole: "student" | "client", params: Record<string, string | string[] | undefined>): Href {
  const q = new URLSearchParams();
  q.set("role", nextRole);
  const email = firstParam(params.email);
  const token = firstParam(params.token);
  if (email) q.set("email", email);
  if (token) q.set("token", token);
  return `/signup?${q.toString()}` as Href;
}

const AMBER = COLORS.AMBER.hex;
const FOREST = COLORS.FOREST.hex;
const SUBTITLE = "rgba(28,43,26,0.68)";

export default function SignupIntroScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ role?: string | string[]; email?: string | string[]; token?: string | string[] }>();
  const roleRaw = firstParam(params.role)?.toLowerCase();

  const role = useMemo(() => {
    if (roleRaw === "student" || roleRaw === "client") return roleRaw;
    return null;
  }, [roleRaw]);

  if (role === "student") return <StudentSignupForm />;
  if (role === "client") return <ClientSignupForm />;

  return (
    <AuthCard>
      {/* Cream-area tree-branch motif — same FOREST stroke style as the
          marketing landing's For Students / Process backgrounds, scaled
          down and pinned to the corners of the sand inner card. The
          parent AuthCard sand container has overflow-hidden, so the
          negative offsets stay clipped to the card's rounded corners. */}
      <SandBackgroundArt />

      <View style={{ zIndex: 1 }}>
        <Text
          className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
          style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
        >
          CREATE ACCOUNT
        </Text>
        <Text
          className="leading-tight"
          style={{ fontFamily: FONTS.display, fontSize: 34, lineHeight: 38, color: FOREST }}
        >
          Who are{" "}
          <Text style={{ fontStyle: "italic", color: AMBER }}>you</Text>{" "}
          signing up as?
        </Text>
        <Text
          className="mt-2 text-[14px] leading-6"
          style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}
        >
          Choose one to continue.
        </Text>

        <View className="mt-8 gap-4">
          <RoleCard
            role="student"
            eyebrow="For Students"
            title="I'm a student"
            description="Join competitions with your school email and build a proof-first profile."
            icon={<IconGraduationCap />}
            onPress={() => router.replace(signupHrefWithRole("student", params))}
          />
          <RoleCard
            role="client"
            eyebrow="For Companies"
            title="I'm a company"
            description="Run recruiting programs and discover talent through live case work."
            icon={<IconBriefcase />}
            onPress={() => router.replace(signupHrefWithRole("client", params))}
          />
        </View>

        <Text
          className="mt-8 text-center text-xs leading-5"
          style={{ fontFamily: FONTS.body, color: "rgba(28,43,26,0.5)" }}
        >
          Invited as a judge or campus admin? Use the link in your invitation email.
        </Text>

        <View className="mt-4">
          <SignInButton onPress={() => router.replace("/login" as Href)} />
        </View>
      </View>
    </AuthCard>
  );
}

/* ─────────────────────────── Role card ─────────────────────────── */

function RoleCard({
  eyebrow,
  title,
  description,
  icon,
  onPress,
}: {
  role: "student" | "client";
  eyebrow: string;
  title: string;
  description: string;
  icon: ReactNode;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }) => {
        const isWebHover = Platform.OS === "web" && hovered;
        return [
          {
            position: "relative",
            paddingVertical: 20,
            paddingLeft: 22,
            paddingRight: 20,
            borderRadius: 6,
            backgroundColor: COLORS.OFFW.hex,
            borderTopWidth: 1,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderTopColor: isWebHover ? "rgba(181,135,58,0.6)" : "rgba(28,43,26,0.10)",
            borderRightColor: isWebHover ? "rgba(181,135,58,0.6)" : "rgba(28,43,26,0.10)",
            borderBottomColor: isWebHover ? "rgba(181,135,58,0.6)" : "rgba(28,43,26,0.10)",
            borderLeftWidth: 2,
            borderLeftColor: isWebHover ? AMBER : "rgba(181,135,58,0.5)",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 16,
            ...(Platform.OS === "web"
              ? ({
                  cursor: "pointer",
                  transitionProperty: "transform, box-shadow, border-color",
                  transitionDuration: "180ms",
                  transitionTimingFunction: "ease-out",
                } as ViewStyle)
              : null),
            ...(isWebHover
              ? ({
                  boxShadow: "0 0 0 1px rgba(181,135,58,0.4), 0 0 24px rgba(181,135,58,0.2)",
                  transform: [{ translateY: -2 }],
                } as ViewStyle)
              : null),
            ...(pressed ? { opacity: 0.96 } : null),
          } as ViewStyle,
        ];
      }}
    >
      <View style={{ width: 32, height: 32, alignItems: "center", justifyContent: "center" }}>
        {icon}
      </View>
      <View style={{ flex: 1 }}>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.5,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: AMBER,
            marginBottom: 6,
          }}
        >
          {eyebrow}
        </Text>
        <Text
          className="text-xl"
          style={{ fontFamily: FONTS.displaySemiBold, color: FOREST }}
        >
          {title}
        </Text>
        <Text
          className="mt-2 text-sm leading-6"
          style={{ fontFamily: FONTS.body, color: "rgba(28,43,26,0.65)" }}
        >
          {description}
        </Text>
      </View>
    </Pressable>
  );
}

/* ─────────────────────────── Sign-in button ─────────────────────────── */

function SignInButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }) => {
        const isHover = Platform.OS === "web" && hovered;
        return [
          {
            paddingVertical: 12,
            paddingHorizontal: 20,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: isHover ? AMBER : "rgba(28,43,26,0.20)",
            backgroundColor: "transparent",
            alignItems: "center",
            ...(Platform.OS === "web"
              ? ({
                  cursor: "pointer",
                  transitionProperty: "border-color, color",
                  transitionDuration: "180ms",
                } as ViewStyle)
              : null),
            ...(pressed ? { opacity: 0.85 } : null),
          } as ViewStyle,
        ];
      }}
    >
      {({ hovered }) => {
        const isHover = Platform.OS === "web" && hovered;
        return (
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11.5,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: isHover ? AMBER : FOREST,
            }}
          >
            Already have an account? Sign in
          </Text>
        );
      }}
    </Pressable>
  );
}

/* ─────────────────────────── Icons (inline SVG) ─────────────────────────── */

// Hand-rolled SVG icons in the Lucide style — lucide-react-native isn't
// installed in this project, and every other icon on Coravu is an inline
// react-native-svg primitive, so stay consistent.

function IconGraduationCap() {
  return (
    <Svg width={32} height={32} viewBox="0 0 24 24" fill="none">
      <Path
        d="M2 10l10-5 10 5-10 5z"
        stroke={AMBER}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <Path
        d="M6 12v4.5c0 1.5 3.5 2.8 6 2.8s6-1.3 6-2.8V12"
        stroke={AMBER}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <Path d="M22 10v5" stroke={AMBER} strokeWidth={1.5} strokeLinecap="round" />
    </Svg>
  );
}

function IconBriefcase() {
  return (
    <Svg width={32} height={32} viewBox="0 0 24 24" fill="none">
      <Rect
        x={2.5}
        y={7}
        width={19}
        height={13.5}
        rx={2}
        stroke={AMBER}
        strokeWidth={1.5}
        fill="none"
      />
      <Path
        d="M8 7V5.5A2 2 0 0 1 10 3.5h4a2 2 0 0 1 2 2V7"
        stroke={AMBER}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <Path d="M2.5 12.5h19" stroke={AMBER} strokeWidth={1.2} strokeLinecap="round" />
    </Svg>
  );
}

/* ─────────────────────────── Background art ─────────────────────────── */

// Subtle FOREST tree-branch motif + a few AMBER dots scattered behind the
// role cards. Mirrors the marketing landing's For Students / Process
// background pattern. pointerEvents=none is mandatory — the cards above
// must stay clickable.
function SandBackgroundArt() {
  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        top: -40,
        right: -36,
        bottom: -40,
        left: -36,
        zIndex: 0,
      }}
    >
      <Svg
        width={420}
        height={420}
        viewBox="0 0 600 600"
        style={{ position: "absolute", right: -80, top: -40, opacity: 0.06 }}
      >
        <G stroke={FOREST} strokeLinecap="round" fill="none">
          <Path
            d="M300 580 C290 470 268 360 244 272 C224 198 200 132 182 76"
            strokeWidth={2.4}
          />
          <Path d="M244 272 C302 246 370 226 432 200 C484 178 526 152 554 128" strokeWidth={1.8} />
          <Path d="M244 272 C186 246 120 226 60 200 C10 178 -30 152 -56 128" strokeWidth={1.8} />
          <Path d="M280 420 C320 410 360 396 392 378" strokeWidth={1.3} />
          <Path d="M268 360 C232 348 196 332 168 312" strokeWidth={1.3} />
        </G>
      </Svg>

      <Svg
        width={340}
        height={340}
        viewBox="0 0 600 600"
        style={{
          position: "absolute",
          left: -100,
          bottom: -80,
          opacity: 0.05,
          transform: [{ rotate: "160deg" }, { scaleX: -1 }],
        }}
      >
        <G stroke={FOREST} strokeLinecap="round" fill="none">
          <Path
            d="M300 580 C292 470 270 360 246 272 C226 200 204 134 188 80"
            strokeWidth={2.2}
          />
          <Path d="M246 272 C310 248 376 228 438 204" strokeWidth={1.6} />
          <Path d="M246 272 C188 248 122 228 60 204" strokeWidth={1.6} />
        </G>
      </Svg>

      {/* AMBER dot scatter — same rhythm as the marketing landing's accents.
          Coordinates are tuned to sit in the negative-space margins so the
          card content above stays uncluttered. */}
      <Svg
        width="100%"
        height="100%"
        viewBox="0 0 400 600"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0 } as ViewStyle}
      >
        <Circle cx={30} cy={70} r={2} fill={AMBER} opacity={0.32} />
        <Circle cx={370} cy={120} r={1.6} fill={AMBER} opacity={0.28} />
        <Circle cx={20} cy={300} r={1.8} fill={AMBER} opacity={0.3} />
        <Circle cx={380} cy={380} r={2.2} fill={AMBER} opacity={0.35} />
        <Circle cx={50} cy={530} r={1.6} fill={AMBER} opacity={0.26} />
        <Circle cx={360} cy={560} r={2} fill={AMBER} opacity={0.3} />
      </Svg>
    </View>
  );
}
